import { UserType, type AuthResponse, type User } from '../types/api';
import { deriveIsParentBrand } from './roles';

/** ROBO-607: the user types the API treats as staff for orders and tasks. */
export const ORDER_STAFF_USER_TYPES: readonly UserType[] = [
    UserType.Admin,
    UserType.Owner,
    UserType.TeamMember
];

/**
 * ROBO-607. Whether the session's EFFECTIVE user type is staff (Admin, Owner or Team Member), which
 * is the rule the API applies to order and task staff writes, to the staff-only order/task fields,
 * and to `GET orders/tasks/{id}` (`TeamRosterProjection.SeesFullRoster(UserTypeID)`).
 *
 * ★ THIS IS `userType`, NOT `isAdmin`. `isAdmin` is `userType === 1 || superUserType === 1`, so an
 * admin using Login As into a group admin, client or assistant still has it, and the API now
 * refuses that session every staff order/task write and strips the staff fields from its reads.
 * The order surfaces follow the effective type so the admin sees what the client sees, which is
 * also what legacy does under Login As (`Sites/summary.asp:1370` reads the session's
 * `nUserTypeID`).
 *
 * An unknown or missing type is not staff (fail closed, like the API). Cosmetic, like every UI
 * gate: the API is the enforcement layer.
 */
export function isEffectiveOrderStaff(userType: UserType | number | null | undefined): boolean {
    return userType != null && ORDER_STAFF_USER_TYPES.includes(userType as UserType);
}

/**
 * API-464 puts the resolved role list on `AuthenticatedResponse.roles`. The access token is
 * encrypted (JWE) and is not a client-side source of claims — session hydration reads this
 * list only.
 */
export function rolesFromAuthResponse(data: AuthResponse): string[] {
    if (!Array.isArray(data.roles)) {
        return [];
    }
    return data.roles.filter((role): role is string => typeof role === 'string');
}

/**
 * UX-837 — whether the signed-in user's paying brand has an active payment processor, for
 * SESSION-scoped payment surfaces (the Payment Types / Bulk Payment nav entries, and Hubby's copies
 * of them). The one place the version-skew rule lives, so the nav and Hubby cannot disagree.
 *
 * ★ ONLY AN EXPLICIT `false` HIDES. `undefined` means the API predates the flag and falls back to
 * SHOW, which is how these entries behaved before it existed. Do not "simplify" to `Boolean(...)`
 * or `?? false`: that turns a version skew into every client losing Payment Types.
 *
 * Order-scoped surfaces (Make Payment, invoice Pay Now) do NOT use this — they follow the order's
 * own brand via `Order.paymentProcessorLinked` (`canTakeCardPayment`).
 */
export function hasSessionPaymentProcessor(
    user: Pick<User, 'hasPaymentProcessor'> | undefined
): boolean {
    return user?.hasPaymentProcessor !== false;
}

export function buildUserFromAuthResponse(data: AuthResponse): User {
    const roles = rolesFromAuthResponse(data);
    return {
        brandId: `${data.brand}`,
        ...data,
        /*
         * UX-837. Replaces a hard-coded `true` that showed Payment Types and Bulk Payment to every
         * eligible user type whether or not their brand could take a card. Carried through verbatim
         * like `canSeeClientPricing` below: `undefined` must stay distinguishable from `false`, because
         * absent means "this API predates the flag" and resolves to SHOW in
         * `hasSessionPaymentProcessor` — today's behaviour — so a UX deploy that lands before the API
         * (UX ~80s, API ~5min) does not strip the nav entries from everyone. A non-boolean is dropped
         * to `undefined` rather than read as either answer.
         */
        hasPaymentProcessor:
            typeof data.hasPaymentProcessor === 'boolean' ? data.hasPaymentProcessor : undefined,
        isAdmin: data.userType === 1 || data.superUserType === 1,
        isParentBrand: deriveIsParentBrand(roles),
        sessionKey: data.sessionKey,
        roles,
        /*
         * ROBO-394. Carried through verbatim, NOT defaulted here. `undefined` has to stay
         * distinguishable from `false` all the way to `isSiteFinancialDataAllowed`, which reads it as
         * "this API predates the flag, derive the rule from roles instead". Collapsing it to a boolean
         * at this boundary would turn a version skew into every team member silently losing pricing.
         */
        canSeeClientPricing: data.canSeeClientPricing,
        /*
         * ROBO-478. Unlike `canSeeClientPricing`, collapsing absent to `false` IS the rule: an API
         * that predates the flag has nothing to say about suspension, and "not suspended" is how
         * every session behaved before it. `=== true` also keeps a non-boolean from reading as set.
         */
        suspended: data.suspended === true
    };
}
